/**
 * Extent detection along the axis. Given a fitted axis + centre + radius, we
 * classify every collected point as on-surface (inlier) or not, bin the
 * inliers by their axial coordinate, then grow a *contiguous* run of populated
 * bins outward from the seed click.
 *
 * Growing contiguously (rather than just taking the global min/max inlier) is
 * what keeps the detection from:
 *   - jumping across an occlusion gap onto a second nearby pipe, and
 *   - running off the end of the pipe into background points that happen to
 *     fall in the radial band.
 * A bend also reads as the run simply ending, because the curved continuation
 * leaves the straight radial band — correct behaviour for straight-only v1.
 */

import { dbg } from './debug';
import { planeBasis } from './linalg';
import type { Vec3 } from './types';

const NSECT = 12; // angular sectors (30° each) for per-bin arc-coverage
const popcount = (m: number): number => {
  let c = 0;
  let x = m;
  while (x) {
    c += x & 1;
    x >>= 1;
  }
  return c;
};

export interface GrowResult {
  /** Axial coordinate of the run start (relative to axisPoint). */
  tStart: number;
  /** Axial coordinate of the run end. */
  tEnd: number;
  /** Indices of inlier points within the supported run. */
  inliers: number[];
  /** Inlier count before the contiguity filter (whole band, any t). */
  totalBandInliers: number;
}

export interface GrowParams {
  axis: Vec3;
  axisPoint: Vec3;
  radius: number;
  /** Inlier band half-width (m): |radialDist − r| < band. */
  band: number;
  /** Axial coordinate of the seed click — the run must include this bin. */
  seedT: number;
  /** Bin width along the axis (m). */
  binStep?: number;
  /** Minimum inliers a bin needs to count as "supported". */
  minPerBin?: number;
}

export function growPipe(positions: Float64Array, count: number, p: GrowParams): GrowResult {
  const binStep = p.binStep ?? Math.max(0.12, p.radius);
  const minPerBin = p.minPerBin ?? 3;

  // Basis for the plane ⟂ axis, so each band point gets a circumferential angle
  // (which sector of the cylinder it sits on). A real pipe cross-section wraps
  // many sectors; a noise blob near the end clusters in one.
  const { e1, e2 } = planeBasis(p.axis);

  // First pass: every corridor point gets an axial t (`allT`). Points on the
  // detected ring (within the band) additionally get a sector. We keep `allT`
  // so we can later count TOTAL corridor points per bin — the ratio
  // ring/total is the bin's "purity".
  const ts: number[] = [];
  const idx: number[] = [];
  const sect: number[] = [];
  const allT = new Float64Array(count);
  const ax = p.axis;
  let minT = Infinity;
  let maxT = -Infinity;
  for (let i = 0; i < count; i++) {
    const dx = positions[3 * i] - p.axisPoint[0];
    const dy = positions[3 * i + 1] - p.axisPoint[1];
    const dz = positions[3 * i + 2] - p.axisPoint[2];
    const t = dx * ax[0] + dy * ax[1] + dz * ax[2];
    allT[i] = t;
    const rx = dx - t * ax[0];
    const ry = dy - t * ax[1];
    const rz = dz - t * ax[2];
    const rd = Math.sqrt(rx * rx + ry * ry + rz * rz);
    if (Math.abs(rd - p.radius) > p.band) continue; // not on the ring
    let ang = Math.atan2(rx * e2[0] + ry * e2[1] + rz * e2[2], rx * e1[0] + ry * e1[1] + rz * e1[2]);
    if (ang < 0) ang += 2 * Math.PI;
    ts.push(t);
    idx.push(i);
    sect.push(Math.min(NSECT - 1, Math.floor((ang / (2 * Math.PI)) * NSECT)));
    if (t < minT) minT = t;
    if (t > maxT) maxT = t;
  }

  const totalBandInliers = idx.length;
  if (totalBandInliers === 0) {
    return { tStart: p.seedT, tEnd: p.seedT, inliers: [], totalBandInliers: 0 };
  }

  // Bin by axial coordinate: ring count, occupied-sector mask, and TOTAL
  // corridor points (the purity denominator).
  const nbins = Math.max(1, Math.ceil((maxT - minT) / binStep) + 1);
  const binCount = new Int32Array(nbins);
  const binSectMask = new Int32Array(nbins);
  const binTotal = new Int32Array(nbins);
  for (let k = 0; k < ts.length; k++) {
    const b = Math.min(nbins - 1, Math.floor((ts[k] - minT) / binStep));
    binCount[b]++;
    binSectMask[b] |= 1 << sect[k];
  }
  for (let i = 0; i < count; i++) {
    const t = allT[i];
    if (t < minT || t > maxT) continue;
    binTotal[Math.min(nbins - 1, Math.floor((t - minT) / binStep))]++;
  }
  const purityOf = (b: number): number => binCount[b] / Math.max(1, binTotal[b]);

  // Adaptive thresholds. A bin is "pipe-like" only if it has enough points, its
  // points wrap the cylinder (many sectors), AND it is PURE — i.e. most
  // corridor points in the bin actually lie on the single detected ring. The
  // purity test is what stops a straight run at a BRANCH: the diverging second
  // pipe (and junction/noise) adds off-ring points, dropping purity, even
  // though the main ring keeps count + sectors high.
  const populated: number[] = [];
  const sectorsPop: number[] = [];
  for (let b = 0; b < nbins; b++) {
    if (binCount[b] > 0) {
      populated.push(binCount[b]);
      sectorsPop.push(popcount(binSectMask[b]));
    }
  }
  populated.sort((a, b) => a - b);
  sectorsPop.sort((a, b) => a - b);
  const medianBin = populated.length > 0 ? populated[populated.length >> 1] : 0;
  const medianSect = sectorsPop.length > 0 ? sectorsPop[sectorsPop.length >> 1] : 0;
  const supportMin = Math.max(minPerBin, Math.round(0.25 * medianBin));
  const sectorMin = Math.max(2, Math.round(0.5 * medianSect));

  // Core purity = median purity among candidate (dense + wrapping) bins, i.e.
  // the clean main pipe. Require bins to stay near that, so a branch/junction
  // (markedly less pure) terminates the run. Floor/ceil keep it sane when the
  // pipe sits in clutter (low core purity) or in clean air (≈1).
  const candPurity: number[] = [];
  for (let b = 0; b < nbins; b++) {
    if (binCount[b] >= supportMin && popcount(binSectMask[b]) >= sectorMin) candPurity.push(purityOf(b));
  }
  candPurity.sort((a, b) => a - b);
  const corePurity = candPurity.length > 0 ? candPurity[candPurity.length >> 1] : 1;
  const purityMin = Math.min(0.85, Math.max(0.3, 0.6 * corePurity));

  const supported = (b: number): boolean =>
    binCount[b] >= supportMin && popcount(binSectMask[b]) >= sectorMin && purityOf(b) >= purityMin;

  const seedBin = Math.max(0, Math.min(nbins - 1, Math.floor((p.seedT - minT) / binStep)));
  // If the seed bin itself is unsupported, snap to the nearest supported bin so
  // a click that landed in a sparse spot still anchors the run.
  let anchor = seedBin;
  if (!supported(anchor)) {
    let best = -1;
    let bestDist = Infinity;
    for (let b = 0; b < nbins; b++) {
      if (supported(b) && Math.abs(b - seedBin) < bestDist) {
        bestDist = Math.abs(b - seedBin);
        best = b;
      }
    }
    if (best < 0) return { tStart: p.seedT, tEnd: p.seedT, inliers: [], totalBandInliers };
    anchor = best;
  }

  // Grow contiguously from the anchor in both directions.
  let lo = anchor;
  let hi = anchor;
  while (lo - 1 >= 0 && supported(lo - 1)) lo--;
  while (hi + 1 < nbins && supported(hi + 1)) hi++;

  // Core-relative trailing trim. Grow can still creep one or two bins into a
  // contiguous-but-degrading tail (noise dense enough to pass `supported`, but
  // thinner / less wrapped than the actual pipe). Measure the run's CORE
  // density + sector coverage, then peel back end bins that fall well below it.
  const coreCounts: number[] = [];
  const coreSects: number[] = [];
  for (let b = lo; b <= hi; b++) {
    coreCounts.push(binCount[b]);
    coreSects.push(popcount(binSectMask[b]));
  }
  coreCounts.sort((a, b) => a - b);
  coreSects.sort((a, b) => a - b);
  const coreMedCount = coreCounts[coreCounts.length >> 1];
  const coreMedSect = coreSects[coreSects.length >> 1];
  const trimCount = 0.5 * coreMedCount;
  const trimSect = Math.max(2, Math.round(0.6 * coreMedSect));
  const weak = (b: number): boolean => binCount[b] < trimCount || popcount(binSectMask[b]) < trimSect;
  while (hi > anchor && weak(hi)) hi--;
  while (lo < anchor && weak(lo)) lo++;

  dbg(
    `grow bins nbins=${nbins} binStep=${binStep.toFixed(3)} support>=${supportMin} sect>=${sectorMin} purity>=${purityMin.toFixed(2)} ` +
      `core(cnt=${coreMedCount},sect=${coreMedSect},purity=${corePurity.toFixed(2)}) run=[${lo}..${hi}] anchor=${anchor}`,
  );
  dbg(
    'grow profile cnt/sect/purity%:',
    Array.from({ length: nbins }, (_, b) => `${binCount[b]}/${popcount(binSectMask[b])}/${Math.round(purityOf(b) * 100)}`).join(' '),
  );

  const runLoT = minT + lo * binStep;
  const runHiT = minT + (hi + 1) * binStep;

  // Collect inliers inside the supported run.
  const inliers: number[] = [];
  const runTs: number[] = [];
  for (let k = 0; k < ts.length; k++) {
    if (ts[k] < runLoT || ts[k] > runHiT) continue;
    inliers.push(idx[k]);
    runTs.push(ts[k]);
  }
  if (inliers.length === 0) {
    return { tStart: p.seedT, tEnd: p.seedT, inliers: [], totalBandInliers };
  }

  // Endpoints from a robust percentile of the inlier axial coords rather than
  // the absolute min/max — a single stray inlier shouldn't push the reported
  // end metres past the real pipe. 0.5% trim removes spikes without
  // meaningfully shortening a well-populated run.
  const sortedT = [...runTs].sort((a, b) => a - b);
  const trim = 0.005;
  const loIdx = Math.floor(trim * (sortedT.length - 1));
  const hiIdx = Math.ceil((1 - trim) * (sortedT.length - 1));
  const tStart = sortedT[loIdx];
  const tEnd = sortedT[hiIdx];

  return { tStart, tEnd, inliers, totalBandInliers };
}
